<script setup lang="ts" generic="TValue extends string | number | boolean | null">
import { computed, inject, useTemplateRef } from "vue";
import {
  CONTROLS_DESIGN_VERSION,
  DEFAULT_CONTROLS_DESIGN_VERSION,
  DEFAULT_DESIGN_VERSION,
  DESIGN_VERSION,
  type Placement,
  type RadioCheckmark,
} from "../models";

export type BcRadioButtonProps<TProp> = {
  /** Specifies the radio button's checkmark style */
  checkmark?: RadioCheckmark;
  /** Disables radio button for selection */
  disabled?: boolean;
  /** Specifies the label of the radio button */
  label?: string | number | boolean;
  /** Specifies the position of the radio button's label */
  labelPlacement?: Placement;
  /** Specifies the name of the radio button */
  name?: string;
  /** Specifies that radio button is read-only */
  readonly?: boolean;
  /** Specifies the value of the radio button */
  value?: TProp;
};

/** Specifies two-way binding for the radio button */
const modelValue = defineModel<TValue>();

const {
  checkmark = "circle",
  disabled = false,
  label = undefined,
  labelPlacement = "right",
  name,
  readonly = false,
  value = false as TValue,
} = defineProps<BcRadioButtonProps<TValue>>();

const containerRef = useTemplateRef("containerRef");
const inputRef = useTemplateRef("inputRef");

const isChecked = computed(() => modelValue.value === value);

function focus(): void {
  // MacOS FireFox and Safari do not focus when clicked
  inputRef.value?.focus();
}

const design = inject(
  DESIGN_VERSION,
  computed(() => DEFAULT_DESIGN_VERSION),
);
const controlsDesign = inject(
  CONTROLS_DESIGN_VERSION,
  computed(() => DEFAULT_CONTROLS_DESIGN_VERSION),
);
const pak = computed(() => `bc-controls-d${controlsDesign.value}`);

function updateInput(event: Event): void {
  const radioInput = event.target as HTMLInputElement;

  switch (typeof value) {
    case "boolean":
      modelValue.value = (radioInput.value === "true") as TValue;
      break;
    case "number":
      modelValue.value = Number.parseFloat(radioInput.value) as TValue;
      break;
    default:
      modelValue.value = radioInput.value as TValue;
      break;
  }
}

defineExpose({
  modelValue,
  containerRef,
  inputRef,
  isChecked,
});
</script>

<template>
  <label
    class="bissantzControls radiobuttonControl"
    v-bind:data-pak="pak"
    v-bind:data-design="design"
    v-bind:class="[
      `label-${labelPlacement}`,
      `checkmark-${checkmark}`,
      {
        disabled: disabled,
        readonly: readonly,
      },
    ]"
    ref="containerRef"
    v-bind:disabled="disabled"
    v-on:click="focus"
    v-on:keydown.prevent.enter="containerRef?.click()"
  >
    <input
      v-show="!readonly"
      type="radio"
      ref="inputRef"
      v-on:click.stop
      v-bind:disabled="disabled"
      v-bind:readonly="readonly"
      v-bind:name="name"
      v-bind:checked="isChecked"
      v-bind:value="value"
      v-on:change="updateInput"
    />
    <span
      class="radioButtonLabel"
      v-bind:class="{ readonly: readonly }"
      v-bind:data-locid="$attrs['data-label-locid']"
      v-bind:data-locparam="$attrs['data-label-locparam']"
      v-bind:data-locparam1="$attrs['data-label-locparam1']"
      v-bind:data-locparam2="$attrs['data-label-locparam2']"
    >
      <slot>{{ label }}</slot>
    </span>
  </label>
</template>

<style lang="less">
@import "../styles/components/bc-radio-button.less";
</style>
